import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EffectsBase } from '@ps/core-data-access';
import { BulkPatchPayloadMapperFactory } from '../../api/mapper/bulk-edit-payload.mapper';
import { ProjectService } from '../../services/project/project.service';
import { SurveyService } from '../../services/survey/survey.service';
import { BulkEditPanelStateManager } from '../../services/bulk-edit/bulk-edit-panel-state.manager';
import { BulkOperationDataValidatorFactory } from '../../services/bulk-edit/domain/validators/bulk-operation-data-validator.factory';

import {
  catchError,
  exhaustMap,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { BulkEditActions, BulkEditApiActions } from './bulk-edit.actions';
import { selectSelectedSurveys } from './bulk-edit.selectors';
import { ToastService } from '@ps/core-overlay';
import { Nullable, PSError } from '@ps/utils';
import { GroupSurveysErrorResponse } from '../../api/dtos/survey-error-response.dto';
import { selectBillingRuleRecord } from '@ps/user-settings-data-access';
import { BillingRuleRecord } from '@ps/user-settings-models';
import { SURVEY_STATUS, SurveyStatus } from '@ps/survey-kernel';
import {
  Survey,
  SurveyHighCpiConfirmationInspector,
  SurveyKind,
} from '@ps/survey-utils';

@Injectable()
export class BulkEditEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _surveyService = inject(SurveyService);
  private readonly _projectService = inject(ProjectService);
  private readonly _toast = inject(ToastService);
  private readonly _stateManager = inject(BulkEditPanelStateManager);
  private readonly _validatorFactory = inject(
    BulkOperationDataValidatorFactory,
  );

  private readonly _bulkPatchPayloadMapperFactory =
    new BulkPatchPayloadMapperFactory();

  public loadBulkStats$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        BulkEditActions.setSelectedSurveys,
        BulkEditActions.resetSelectedSurveys,
      ),
      concatLatestFrom(() => this._store.select(selectSelectedSurveys)),
      map(([, selecteds]) => selecteds.map((selected) => selected.id)),
      switchMap((selectedIds) =>
        this._projectService.getSurveyBulkStats(selectedIds).pipe(
          map((stats) => BulkEditApiActions.loadBulkStatsSuccess({ stats })),
          this.psCatchError(
            () => of(BulkEditApiActions.loadBulkStatsSuccess({ stats: null })),
            {
              notify: true,
              customMessage:
                'Bulk summary could not be fetched. Please try again!',
            },
          ),
        ),
      ),
    ),
  );

  public bulkPatchValues$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.bulkPatchValues),
      filter(() => !!this._stateManager.records().length),
      map(({ operation, operationLabel }) => ({
        operation,
        operationLabel,
        validationResult: this._validatorFactory.validateBulkPatch(operation),
      })),
      switchMap(({ operation, operationLabel, validationResult }) => {
        if (!validationResult.isValid) {
          return of(
            BulkEditApiActions.bulkPatchValuesFailure({
              error:
                validationResult.error ??
                `Validation failed for ${operationLabel}.`,
            }),
          );
        }
        const mapper = this._bulkPatchPayloadMapperFactory.create(operation);
        if (!mapper) {
          return of(
            BulkEditApiActions.bulkPatchValuesFailure({
              error: `Bulk operation ${operation} failed to initiate because no matching mapper was found.`,
            }),
          );
        }
        const entries = this._stateManager.records().map((row) => ({
          surveyId: row['id'] as number,
          payload: mapper.toPayload(row),
        }));

        this._stateManager.startOperation();
        return forkJoin(
          entries.map(({ surveyId, payload }) =>
            this._surveyService.patchSurvey(surveyId, payload).pipe(
              tap(() => this._stateManager.deleteRecord(surveyId)),
              map(() => ({ surveyId, error: null })),
              this.psCatchError((err: PSError) =>
                of({ surveyId, error: err.message }),
              ),
            ),
          ),
        ).pipe(
          map((results) => {
            const failures = results.filter((r) => r.error !== null);
            if (!failures.length) {
              this._toast.success(
                `Bulk Operation completed successfully: ${operationLabel}`,
                { keepOpen: false },
              );
              this._stateManager.finishOperation();
              this._stateManager.complete();
              return BulkEditApiActions.bulkPatchValuesSuccess();
            }
            for (const failure of failures) {
              this._stateManager.updateDefaultErrorInRecord(
                failure.surveyId,
                failure.error,
              );
            }
            this._stateManager.finishOperation();
            const failedIds = failures.map((f) => f.surveyId).join(', ');
            return BulkEditApiActions.bulkPatchValuesFailure({
              error: `Patch failed for survey IDs: ${failedIds}`,
            });
          }),
        );
      }),
    ),
  );

  public launchTcReplica$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.launchTcReplica),
      filter(() => !!this._stateManager.records().length),
      switchMap(({ operationLabel }) => {
        return forkJoin(
          this._stateManager.records().map((row) => {
            const surveyId = row['id'] as number;
            return this._surveyService.launchTcReplicaBulk(surveyId).pipe(
              tap(() => this._stateManager.deleteRecord(surveyId)),
              map(() => ({ surveyId, error: null })),
              this.psCatchError((err: PSError) =>
                of({ surveyId, error: err.message }),
              ),
            );
          }),
        ).pipe(
          map((results) => {
            const failures = results.filter((r) => r.error !== null);
            if (!failures.length) {
              this._toast.success(
                `Bulk Operation completed successfully: ${operationLabel}`,
                { keepOpen: false },
              );
              this._stateManager.complete();
              return BulkEditApiActions.launchTcReplicaSuccess();
            }
            for (const failure of failures) {
              this._stateManager.updateDefaultErrorInRecord(
                failure.surveyId,
                failure.error,
              );
            }
            const failedIds = failures.map((f) => f.surveyId).join(', ');
            const errorMessage = `Failed to launch TC Replica for survey IDs: ${failedIds}`;
            return BulkEditApiActions.launchTcReplicaFailure({
              error: errorMessage,
            });
          }),
        );
      }),
    ),
  );

  public deleteDraft$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.deleteDraft),
      filter(() => !!this._stateManager.records().length),
      switchMap(({ operationLabel }) => {
        return forkJoin(
          this._stateManager.records().map((row) => {
            const surveyId = row['id'] as number;
            return this._surveyService.deleteSurvey(surveyId).pipe(
              tap(() => this._stateManager.deleteRecord(surveyId)),
              map(() => ({ surveyId, error: null })),
              this.psCatchError((err: PSError) =>
                of({ surveyId, error: err.message }),
              ),
            );
          }),
        ).pipe(
          map((results) => {
            const failures = results.filter((r) => r.error !== null);
            if (!failures.length) {
              this._toast.success(
                `Bulk Operation completed successfully: ${operationLabel}`,
                {
                  keepOpen: false,
                },
              );
              this._stateManager.complete();
              return BulkEditApiActions.deleteDraftSuccess();
            }
            for (const failure of failures) {
              this._stateManager.updateDefaultErrorInRecord(
                failure.surveyId,
                failure.error,
              );
            }
            const failedIds = failures.map((f) => f.surveyId).join(', ');
            const errorMessage = `Failed to delete surveys: ${failedIds}`;
            return BulkEditApiActions.deleteDraftFailure({
              error: errorMessage,
            });
          }),
        );
      }),
    ),
  );

  public checkSurveysWithMatchingBillingId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.checkSurveysWithMatchingBillingID),
      filter(({ billingId }) => !!billingId),
      switchMap(({ billingId }) =>
        this._surveyService.searchInvoicedSurveysWithBillingId(billingId).pipe(
          map(() =>
            BulkEditApiActions.checkSurveysWithMatchingBillingIDSuccess(),
          ),
          this.psCatchError(() => {
            this._toast.warning(
              'This BILLING CODE has already been used for another invoiced surveys.',
              {
                keepOpen: false,
              },
            );
            return of(
              BulkEditApiActions.checkSurveysWithMatchingBillingIDFailure(),
            );
          }),
        ),
      ),
    ),
  );

  public validateBillingID$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.validateBillingID),
      concatLatestFrom(() => this._store.select(selectBillingRuleRecord)),
      map(
        ([{ billingId }, billingRuleRecord]) =>
          <[string, Nullable<BillingRuleRecord>]>[billingId, billingRuleRecord],
      ),
      filter(
        ([billingId, billingRuleRecord]) => !!billingId && !!billingRuleRecord,
      ),
      switchMap(([billingId, billingRuleRecord]) =>
        this._surveyService
          .verifyBillingIdAgainstBillingRules(billingId, billingRuleRecord!)
          .pipe(
            map(() => BulkEditApiActions.validateBillingIDSuccess()),
            this.psCatchError((err) => {
              this._stateManager.updateDefaultErrorInRecords(err.message);
              this._toast.error(err.message, { keepOpen: false });
              return of(BulkEditApiActions.validateBillingIDFailure());
            }),
          ),
      ),
    ),
  );

  public validateHighCpiOnStatusChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.validateHighCpiOnStatusChange),
      filter(({ status }) => status === SURVEY_STATUS.LIVE),
      concatLatestFrom(() => this._store.select(selectSelectedSurveys)),
      map(
        ([{ status }, selectedSurveys]) =>
          <[SurveyStatus, Survey[]]>[
            status,
            selectedSurveys.filter((s) => s.status === SURVEY_STATUS.PAUSED),
          ],
      ),
      filter(([, eligibleSurveys]) => !!eligibleSurveys.length),
      switchMap(([status, eligibleSurveys]) =>
        forkJoin(
          eligibleSurveys.map((survey) =>
            this._surveyService.getHighCpiDetails(survey.id).pipe(
              map((response) => ({ survey, response, error: null })),
              this.psCatchError((err: PSError) =>
                of({ survey, response: null, error: err.message }),
              ),
            ),
          ),
        ).pipe(
          map((results) => {
            const successResponses = results.filter(
              (result) => result.error === null,
            );
            const failureResponses = results.filter(
              (result) => result.error !== null,
            );

            for (const { survey, response } of successResponses) {
              const inspector = new SurveyHighCpiConfirmationInspector({
                type: 'high-cpi-data',
                nextStatus: status,
                highCpiDetails: response,
                isProject: SurveyKind.isProject(survey),
              });
              if (inspector.check()) {
                const highCpiError = inspector.text();
                this._stateManager.updateApprovableErrorInRecord(
                  survey.id,
                  highCpiError,
                );
              }
            }

            for (const { survey, error } of failureResponses) {
              this._stateManager.updateDefaultErrorInRecord(survey.id, error);
            }

            if (!successResponses.length) {
              return BulkEditApiActions.validateHighCpiOnStatusChangeFailure();
            }
            return BulkEditApiActions.validateHighCpiOnStatusChangeSuccess();
          }),
        ),
      ),
    ),
  );

  public downloadSupplierSummaryReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.getSupplierSummaryReport),
      switchMap(({ email, surveys }) =>
        this._projectService.getCombinedSummaryReport(email, surveys).pipe(
          tap(({ message }) =>
            this._toast.success(message, { keepOpen: false }),
          ),
          map(() => BulkEditApiActions.downloadSupplierSummaryReportSuccess()),
          this.psCatchError(
            () => of(BulkEditApiActions.downloadSupplierSummaryReportFailure()),
            {
              notify: true,
              customMessage:
                'Bulk summary could not be fetched. Please try again!',
            },
          ),
        ),
      ),
    ),
  );

  public groupSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BulkEditActions.groupSurveys),
      concatLatestFrom(() => this._store.select(selectSelectedSurveys)),
      exhaustMap(([{ groupTitle, operationLabel }, selectedSurveys]) => {
        const surveyIds = selectedSurveys.map((s) => s.id);
        return this._surveyService.groupSurveys(surveyIds, groupTitle).pipe(
          tap(() => {
            this._toast.success(
              `Bulk Operation completed successfully: ${operationLabel}`,
              { keepOpen: false },
            );
            this._stateManager?.complete();
          }),
          map(() => BulkEditApiActions.groupSurveysSuccess()),
          catchError((err: PSError) => {
            const { invalidSurveyIds, message } =
              this._extractGroupSurveysResponseData(err, surveyIds);
            invalidSurveyIds?.forEach((id) => {
              this._stateManager?.updateDefaultErrorInRecord(id, message);
            });

            return of(
              BulkEditApiActions.groupSurveysFailure({
                error: message,
              }),
            );
          }),
        );
      }),
    ),
  );

  private _extractGroupSurveysResponseData(
    err: PSError,
    surveyIds: number[],
  ): {
    invalidSurveyIds: number[];
    message: string;
  } {
    const httpError = err.metadata?.['http'] as
      | { error?: GroupSurveysErrorResponse }
      | undefined;

    const invalidIds =
      httpError?.error?.ps_api_response_data?.invalidSurveyIds ?? [];

    return {
      invalidSurveyIds: invalidIds.length > 0 ? invalidIds : surveyIds,
      message: err.message,
    };
  }
}
